import { ColDef, ValueFormatterParams, ValueGetterParams } from "ag-grid-community";
import dayjs from "dayjs";
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import AgGridCustomTable from "../../../util/component/AgGridCustomTable";
import CustomLoader from "../../../util/component/CustomLoader";
import { getCustomerBaseChangeLogAsync } from "../api/service/customerbaseApi";
import { CBChangeLogRow } from "../types";

interface CustomerBaseChangeLogTabProps {
    cbCustomerId?: string | number;
}

/**
 * TAB "Change log" del dettaglio cliente (System Console) — CR-DRIVALIA-EPCH (LPP-22837).
 * Mirror del changelog di Booking (stream/booking/.../change-log/TabChangeLog.tsx).
 * Per ora traccia i cambi di e-mail e numero di telefono. Colonna Editor:
 * System | User | AMS | codice operatore (risolto lato CB).
 */
const CustomerBaseChangeLogTab = ({ cbCustomerId }: CustomerBaseChangeLogTabProps) => {

    const { t } = useTranslation("customerbase");

    const query = useQuery(
        ["getCustomerBaseChangeLog", cbCustomerId],
        () => getCustomerBaseChangeLogAsync(cbCustomerId),
        { enabled: !!cbCustomerId }
    );

    const columnDefs = useMemo<ColDef<CBChangeLogRow>[]>(() => [
        {
            field: "datetime",
            headerName: t("changeLog.timestamp"),
            sortable: true,
            sort: "desc",
            valueFormatter: (p: ValueFormatterParams<CBChangeLogRow, string>) =>
                p.value ? dayjs(p.value).format("DD-MM-YYYY HH:mm") : ""
        },
        {
            field: "operator",
            headerName: t("changeLog.editor"),
            valueGetter: (p: ValueGetterParams<CBChangeLogRow>) => p.data?.operator?.username ?? "-"
        },
        {
            field: "fieldName",
            headerName: t("changeLog.fieldName"),
            valueFormatter: (p: ValueFormatterParams<CBChangeLogRow, string>) =>
                p.value ? (t(`changeLog.fields.${p.value}`, p.value) as string) : ""
        },
        {
            field: "originalValue",
            headerName: t("changeLog.originalValue")
        },
        {
            field: "newValue",
            headerName: t("changeLog.newValue")
        }
    ], [t]);

    const defaultColDef = useMemo<ColDef>(() => ({ resizable: true, unSortIcon: true }), []);

    const rowData = useMemo<CBChangeLogRow[]>(() => (query.data?.data ?? []), [query.data]);

    return (
        <>
            <AgGridCustomTable<CBChangeLogRow>
                height="500px"
                columnDefs={columnDefs}
                defaultColDef={defaultColDef}
                rowData={rowData}
                pagination={false}
            />
            <CustomLoader loading={query.isLoading} />
        </>
    );
};

export default CustomerBaseChangeLogTab;
